import { useEffect, useState } from "react";
import { Clock, X, Loader2, RefreshCw, ShieldAlert, Save, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export function PendingDelaysPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(10);
  const [savingLimit, setSavingLimit] = useState(false);
  const [digest, setDigest] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);
  const load = () => api.get("/automations/delays").then((r) => { setData(r.data); setLimit(r.data.limit_per_hour); }).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then((r) => setDigest(r.data.daily_digest_enabled !== false)).catch(() => {}); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const cancelAll = async (automationId, name, n) => {
    if (!window.confirm(`Annullare tutte le ${n} attese dell'automazione «${name}»?`)) return;
    try { const { data } = await api.delete("/automations/delays", { params: { automation_id: automationId } }); toast.success(`${data.deleted} attese annullate`); load(); }
    catch { toast.error("Operazione non riuscita"); }
  };
  const toggleDigest = async (v) => {
    setDigest(v);
    try { await api.put("/settings", { daily_digest_enabled: v }); toast.success(v ? "Riepilogo serale attivo (ogni giorno alle 20:00)" : "Riepilogo serale disattivato"); }
    catch { toast.error("Salvataggio non riuscito"); }
  };
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try { const { data } = await api.post("/automations/daily-digest/send-now"); toast[data.sent ? "success" : "warning"](data.sent ? `Riepilogo inviato a ${data.to}` : `Non inviato: ${data.reason === "no_email" ? "imposta l'email admin nelle Impostazioni" : data.reason}`); }
    catch { toast.error("Invio non riuscito"); }
    finally { setSendingDigest(false); }
  };

  const cancel = async (d) => {
    try { await api.delete(`/automations/delays/${d.id}`); toast.success(`Attesa annullata per ${d.contact_name || d.phone}`); load(); }
    catch { toast.error("Attesa non trovata (forse già inviata)"); load(); }
  };
  const saveLimit = async () => {
    setSavingLimit(true);
    try { await api.put("/settings", { auto_send_limit_per_hour: Number(limit) }); toast.success(`Freno impostato a ${limit} invii/ora per cliente`); load(); }
    catch { toast.error("Salvataggio non riuscito"); }
    finally { setSavingLimit(false); }
  };
  const items = data?.items || [];
  const overdue = items.filter((i) => i.overdue).length;
  const groups = Object.values(items.reduce((acc, i) => { acc[i.automation_id] = acc[i.automation_id] || { id: i.automation_id, name: i.automation_name, n: 0 }; acc[i.automation_id].n += 1; return acc; }, {}));
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="pending-delays-panel">
      <button type="button" onClick={() => setOpen(!open)} data-testid="pending-delays-toggle" className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Clock className="h-4 w-4 text-[#F59E0B]" /> Attese programmate
          <span className="rounded-full bg-[#F59E0B]/15 text-[#B45309] px-2 py-0.5 text-xs" data-testid="pending-delays-count">{items.length}</span>
          {overdue > 0 && <span className="text-xs text-[#EF4444]">· {overdue} in invio</span>}
        </span>
        <span className="text-xs text-slate-500">{open ? "Nascondi" : "Mostra"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 bg-slate-50 rounded-lg p-2" data-testid="auto-send-limit-box">
            <ShieldAlert className="h-4 w-4 text-[#EF4444]" /> Freno di sicurezza: massimo
            <Input type="number" min="1" max="500" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="auto-send-limit-input" className="h-7 w-20 bg-white border-slate-200 text-slate-800 text-xs" />
            passaggi automatici all'ora per cliente; oltre, sequenze e attese vengono interrotte ed arriva un'email all'admin.
            <Button size="sm" onClick={saveLimit} disabled={savingLimit} data-testid="auto-send-limit-save" className="h-7 bg-[#5BC65F] hover:bg-[#45B049] text-white ml-auto">
              {savingLimit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Salva
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 bg-slate-50 rounded-lg p-2" data-testid="daily-digest-box">
            <Mail className="h-4 w-4 text-[#3B82F6]" /> Riepilogo serale all'admin (ore 20:00): invii di campagne e automazioni, STOP, freni scattati e attese in corso.
            <Switch checked={digest} onCheckedChange={toggleDigest} data-testid="daily-digest-switch" />
            <Button size="sm" variant="outline" onClick={sendDigestNow} disabled={sendingDigest} data-testid="daily-digest-send-now" className="h-7 ml-auto bg-white border-slate-200 text-slate-700">
              {sendingDigest ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />} Invia ora
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Clienti fermi su un nodo «Attendi»: il messaggio successivo parte all'orario indicato.</p>
            <button type="button" onClick={load} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="pending-delays-refresh"><RefreshCw className="h-3 w-3" /> Aggiorna</button>
          </div>
          {groups.length > 1 || (groups.length === 1 && groups[0].n > 1) ? (
            <div className="flex flex-wrap gap-1.5" data-testid="pending-delays-groups">
              {groups.map((g) => (
                <button key={g.id} type="button" onClick={() => cancelAll(g.id, g.name, g.n)} data-testid={`pending-delays-cancel-all-${g.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[#EF4444]/30 text-[#B91C1C] hover:bg-[#EF4444]/10 px-2.5 py-0.5 text-xs">
                  <X className="h-3 w-3" /> Annulla tutte «{g.name}» ({g.n})
                </button>
              ))}
            </div>
          ) : null}
          {!items.length ? <p className="text-sm text-slate-500 py-2" data-testid="pending-delays-empty">Nessuna attesa in corso.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-1.5 pr-2">Cliente</th><th className="py-1.5 pr-2">Automazione</th><th className="py-1.5 pr-2">Prossimo nodo</th><th className="py-1.5 pr-2">Invio previsto</th><th className="py-1.5" />
              </tr></thead>
              <tbody data-testid="pending-delays-table">
                {items.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100" data-testid={`pending-delay-${d.id}`}>
                    <td className="py-1.5 pr-2 text-slate-800">{d.contact_name || d.phone}{d.contact_name && <span className="block text-[11px] text-slate-400 font-mono">{d.phone}</span>}</td>
                    <td className="py-1.5 pr-2 text-slate-700">{d.automation_name}{!d.automation_enabled && <span className="ml-1 text-[10px] text-slate-400">(disattivata)</span>}</td>
                    <td className="py-1.5 pr-2 text-xs text-slate-600">{d.step_text || d.step_key}{d.template_name && <span className="block text-[11px] text-[#8B5CF6]">template {d.template_name}</span>}</td>
                    <td className={`py-1.5 pr-2 text-xs ${d.overdue ? "text-[#EF4444] font-semibold" : "text-slate-600"}`}>{fmt(d.due_at)}</td>
                    <td className="py-1.5 text-right">
                      <button type="button" onClick={() => cancel(d)} data-testid={`pending-delay-cancel-${d.id}`} title="Annulla questa attesa" className="text-slate-400 hover:text-[#EF4444]"><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
